import type { ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// Único ponto de verdade para as ações de veículo (card da lista, popup do
// mapa — e qualquer outro lugar futuro): mesma lista de itens, mesmos
// rótulos, mesma ordem. Consumidores que precisam de outro tipo de menu
// (ex.: o botão direito do card usa ContextMenuItem, não DropdownMenuItem)
// reaproveitam `getVehicleActionItems` diretamente em vez de duplicar a
// lista — só o componente <VehicleActionsMenu> abaixo é específico de
// dropdown.
export interface VehicleActionHandlers {
  onCommand: () => void;
  onShowHistory: () => void;
  onFollow: () => void;
  onShowTrailPanel: () => void;
  onEdit: () => void;
  following: boolean;
  // Bug real 2026-09-01: "Enviar comando" e "Editar" apareciam pra
  // QUALQUER usuário logado neste menu, sem checar as permissões
  // granulares commands.send_command/devices.edit — um usuário sem
  // nenhuma permissão ativa (ex.: relatado com WALTOR DA SILVA PIRES)
  // conseguia editar o cadastro do veículo e mandar comando pelo app
  // mesmo com tudo desligado na aba PERMISSÕES. Único ponto que monta
  // este menu (lista lateral e popup do mapa, ver routes/index.tsx) —
  // omitir os itens aqui fecha os dois lugares de uma vez. Item
  // simplesmente não aparece quando falso (mesmo espírito de
  // "fail closed" do usePermission), em vez de aparecer desabilitado.
  canEdit: boolean;
  canSendCommand: boolean;
}

export interface VehicleActionItem {
  key: string;
  emoji: string;
  label: string;
  onSelect: () => void;
}

export function getVehicleActionItems(h: VehicleActionHandlers): VehicleActionItem[] {
  const items: VehicleActionItem[] = [];
  if (h.canSendCommand) {
    items.push({ key: "command", emoji: "📡", label: "Enviar comando", onSelect: h.onCommand });
  }
  items.push({ key: "history", emoji: "🕓", label: "Mostrar Histórico", onSelect: h.onShowHistory });
  items.push({ key: "follow", emoji: "🎯", label: h.following ? "Parar de seguir" : "Seguir veículo", onSelect: h.onFollow });
  items.push({ key: "trail", emoji: "👣", label: "Mostrar Rastro", onSelect: h.onShowTrailPanel });
  if (h.canEdit) {
    items.push({ key: "edit", emoji: "✏️", label: "Editar", onSelect: h.onEdit });
  }
  return items;
}

export function VehicleActionsMenu({
  handlers,
  children,
  align = "end",
  className,
}: {
  handlers: VehicleActionHandlers;
  children: ReactNode;
  align?: "start" | "end";
  className?: string;
}) {
  const items = getVehicleActionItems(handlers);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        // z-[9999]: a sidebar da lista de frota já usa z-[1000] (ver
        // src/routes/index.tsx), e o portal do Radix é montado direto no
        // <body> — sem isso o menu abre por baixo do painel e some (mesmo
        // motivo do z-[9999] no ContextMenuContent do clique direito).
        className={cn("z-[9999]", className ?? "w-56")}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {items.map((item) => (
          <DropdownMenuItem key={item.key} onSelect={item.onSelect}>
            <span className="mr-2">{item.emoji}</span>
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
